import { StepForm } from "Components/StepForm";
import { useEffect, useRef, useState } from "react";
import { ProgramAndCountry } from "./ProgramAndCountry";
import { ApplyingFor } from "./ApplyingFor";
import { OtherPreferences } from "./OtherPreferences";
import { TestScores } from "./TestScores";
import { Alert, Button, Flex, Form, notification, Spin, Typography } from "antd";
import { EducationAndExperience } from "./EducationAndExperience";
import { useNavigate, useSearchParams } from "react-router-dom";
import { registerNewUserViaGoogle } from "Features/Auth/apis/registerUser";
import {
  findCollegesApi,
  updateCollegeFinderDetails,
} from "./apis/collegeFinderForm";
import { useMutation } from "@tanstack/react-query";
import { useUserInfo } from "GlobalStates/User";
import { useGoogleLogin } from "@react-oauth/google";
import { toast } from "react-toastify";
import { ROUTES } from "Features/Routes/routeConstants";
import { useRecoilValue } from "recoil";
import { cookieConsentState } from "GlobalStates/CookieConsent";
import ReactGA from "react-ga4";
import { trackFunnelEvent, trackJourneyEvent } from "Config/analytics";
import {
  clearRecommendationRequestContext,
  completeCollegeFinderRegistration,
  createJourneyCorrelationId,
  getRecommendationRequestContext,
  MISSING_ACCESS_TOKEN,
  rememberRecommendationCorrelation,
} from "./registrationFlow";
import "./styles.css";

const steps = [
  {
    title: "Program & Country",
  },
  {
    title: "Applying For",
  },
  {
    title: "Education",
  },
  {
    title: "Test Scores",
  },
  {
    title: "Preferences",
  },
];

const stepFormTitles = [
  "Program & Country",
  "Applying For",
  "Previous Education & Experience",
  "Test Scores",
  "Other Preferences",
];

export const CollegeFinder = () => {
  const { Title } = Typography;
  const [api, contextHolder] = notification.useNotification();
  const [currentStep, setCurrentStep] = useState(0);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [recommendationError, setRecommendationError] = useState(null);
  const activeRecommendationFlow = useRef(null);
  const mounted = useRef(true);
  const retryAction = useRef(null);
  const registrationCorrelationId = useRef(null);
  const navigate = useNavigate();
  const { user, setUser } = useUserInfo();
  const [form] = Form.useForm();
  const consent = useRecoilValue(cookieConsentState);
  const [searchParams] = useSearchParams();

  const stepFormElements = [
    <ProgramAndCountry form={form} />,
    <ApplyingFor form={form} />,
    <EducationAndExperience form={form} />,
    <TestScores form={form} />,
    <OtherPreferences form={form} />,
  ];
  const generateRecommendationsMutation = useMutation({
    mutationFn: findCollegesApi,
  });

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const runRegistrationRecommendations = async (authData) => {
    let flowId;
    let ownsTerminalState = false;
    setRecommendationError(null);
    setLoadingRecommendations(true);

    try {
      const startedAt = Date.now();
      const outcome = await completeCollegeFinderRegistration({
        authData,
        setUser,
        generateRecommendations: (variables) =>
          generateRecommendationsMutation.mutateAsync(variables),
        onFlowStart: (startedFlowId, requestContext, duplicate) => {
          flowId = startedFlowId;
          activeRecommendationFlow.current = startedFlowId;
          trackJourneyEvent(
            duplicate ? "duplicate_suppressed" : "generation_started",
            {
              source: "college_finder_registration",
              correlation_id: requestContext.correlationId,
              replayed: duplicate,
            }
          );
        },
        isFlowActive: (candidateFlowId) =>
          mounted.current && activeRecommendationFlow.current === candidateFlowId,
      });
      ownsTerminalState = outcome.terminalOwner;
      if (!ownsTerminalState) return;

      trackJourneyEvent("generation_succeeded", {
        source: "college_finder_registration",
        correlation_id: outcome.requestContext.correlationId,
        duration_ms: Date.now() - startedAt,
        terminal_status: "succeeded",
      });
      trackFunnelEvent("recommendations_generated", {
        source: "college_finder_registration",
        correlation_id: outcome.requestContext.correlationId,
      });
      if (authData.message === "user already exists") {
        toast.success("Welcome back " + authData.name);
      } else if (authData.message === "user created") {
        toast.success("Welcome " + authData.name);
      }
    } catch (error) {
      ownsTerminalState = error?.terminalOwner ?? !flowId;
      if (!ownsTerminalState) return;

      const authenticationFailure = error?.code === MISSING_ACCESS_TOKEN;
      trackJourneyEvent(
        authenticationFailure ? "auth_contract_failed" : "generation_failed",
        {
          correlation_id: error?.requestContext?.correlationId,
          error_category: authenticationFailure
            ? "missing_access_token"
            : "recommendation_failure",
          terminal_status: "failed",
        }
      );
      setRecommendationError({
        category: authenticationFailure
          ? "authentication_failure"
          : "recommendation_failure",
        message: authenticationFailure
          ? "Authentication failed. Please sign in again."
          : "We could not generate your recommendations. Your profile is saved and you can retry.",
      });
      retryAction.current = authenticationFailure
        ? () => googleLogin()
        : () => runRegistrationRecommendations(authData);
    } finally {
      if (
        ownsTerminalState &&
        mounted.current &&
        (!flowId || activeRecommendationFlow.current === flowId)
      ) {
        setLoadingRecommendations(false);
      }
    }
  };

  const registerUserMutation = useMutation({
    mutationFn: registerNewUserViaGoogle,
    onSuccess: async (data, variables) => {
      const authOutcome =
        data?.data?.message === "user created" ? "sign_up" : "login";

      trackFunnelEvent(authOutcome, {
        method: "google",
        auth_surface: "college_finder",
        correlation_id: variables?.correlationId,
      });
      trackJourneyEvent("authentication_succeeded", {
        correlation_id: variables?.correlationId,
        terminal_status: "succeeded",
      });
      await runRegistrationRecommendations({
        ...data.data,
        correlationId: variables?.correlationId,
      });
    },
    onError: (error) => {
      trackJourneyEvent("authentication_failed", {
        correlation_id: registrationCorrelationId.current,
        error_category: error?.code || "google_authentication",
        terminal_status: "failed",
      });
      setLoadingRecommendations(false);
      setRecommendationError({
        category: "authentication_failure",
        message: "Google sign-in failed. Please try signing in again.",
      });
      retryAction.current = () => googleLogin();
    },
  });

  const googleLogin = useGoogleLogin({
    onSuccess: (user) => {
      registerUserMutation.mutate({
        token: user?.access_token,
        preferences: form.getFieldsValue(true),
        correlationId:
          registrationCorrelationId.current || createJourneyCorrelationId(),
      });
    },
  });

  const runAuthenticatedRecommendations = async () => {
    const requestContext = getRecommendationRequestContext(user.id);
    const flowId = `update.${requestContext.idempotencyKey}`;
    activeRecommendationFlow.current = flowId;
    setRecommendationError(null);
    setLoadingRecommendations(true);

    try {
      trackJourneyEvent("generation_started", {
        source: "college_finder_update",
        correlation_id: requestContext.correlationId,
      });
      const startedAt = Date.now();
      await generateRecommendationsMutation.mutateAsync({
        accessToken: user.token,
        ...requestContext,
      });
      if (!mounted.current || activeRecommendationFlow.current !== flowId) return;

      clearRecommendationRequestContext(user.id);
      rememberRecommendationCorrelation(user.id, requestContext.correlationId);
      trackJourneyEvent("generation_succeeded", {
        source: "college_finder_update",
        correlation_id: requestContext.correlationId,
        duration_ms: Date.now() - startedAt,
        terminal_status: "succeeded",
      });
      trackFunnelEvent("recommendations_generated", {
        source: "college_finder_update",
        correlation_id: requestContext.correlationId,
      });
      setUser((previousUser) => ({
        ...previousUser,
        completedCollegeFinder: true,
      }));
    } catch (error) {
      if (!mounted.current || activeRecommendationFlow.current !== flowId) return;
      trackJourneyEvent("generation_failed", {
        source: "college_finder_update",
        correlation_id: requestContext.correlationId,
        error_category: error?.response?.data?.error?.code || "generation_failure",
        terminal_status: "failed",
      });
      setRecommendationError({
        category: "recommendation_failure",
        message:
          "We could not refresh your recommendations. Your profile is saved and you can retry.",
      });
      retryAction.current = runAuthenticatedRecommendations;
    } finally {
      if (mounted.current && activeRecommendationFlow.current === flowId) {
        setLoadingRecommendations(false);
      }
    }
  };

  const updateCollegeFinderMutation = useMutation({
    mutationFn: updateCollegeFinderDetails,
    onSuccess: async () => {
      trackFunnelEvent("college_finder_submit", {
        user_state: "authenticated",
        step_index: currentStep,
        step_name: stepFormTitles[currentStep],
      });
      await runAuthenticatedRecommendations();
    },
    onError: (error) => {
      const correlationId = createJourneyCorrelationId();
      trackJourneyEvent("persistence_failed", {
        correlation_id: correlationId,
        error_category:
          error?.response?.data?.error?.code || "profile_persistence_failure",
        terminal_status: "failed",
      });
      setLoadingRecommendations(false);
      setRecommendationError({
        category: "profile_persistence_failure",
        message: "Your profile could not be saved. Please retry.",
      });
      retryAction.current = () => {
        setRecommendationError(null);
        updateCollegeFinderMutation.mutate({
          preferences: form.getFieldsValue(true),
        });
      };
    },
  });

  const updateCollegeFinderPartial = useMutation({
    mutationFn: updateCollegeFinderDetails,
    onError: (error) => {
      api.open({
        message: "Oops! We have a problem",
        duration: 5,
      });
    },
  });

  useEffect(() => {
    if (user?.token && user?.completedCollegeFinder) {
      if (searchParams?.get("next")) {
        trackJourneyEvent("navigation", {
          destination: searchParams.get("next"),
          source: "college_finder",
        });
        navigate(searchParams?.get("next"), { replace: true, state: {} });
      } else {
        trackJourneyEvent("navigation", {
          destination: ROUTES.DASHBOARD,
          source: "college_finder",
        });
        navigate(ROUTES.DASHBOARD);
      }
    }
  }, [user, navigate, searchParams]);

  useEffect(() => {
    trackFunnelEvent("college_finder_step_view", {
      step_index: currentStep,
      step_name: stepFormTitles[currentStep],
      user_state: user?.token ? "authenticated" : "anonymous",
    });
  }, [currentStep, user?.token]);

  const onFinish = async () => {
    try {
      await form.validateFields();
      if (!consent) {
        trackFunnelEvent("cookie_consent_required", {
          funnel_step: "college_finder_submit",
        });
        toast.error("Please allow cookies to use this feature");
        return;
      }
      if (!user?.token) {
        trackFunnelEvent("college_finder_submit", {
          user_state: "anonymous",
          step_index: currentStep,
          step_name: stepFormTitles[currentStep],
        });
        trackFunnelEvent("registration_start", {
          method: "google",
          auth_surface: "college_finder",
          step_index: currentStep,
          step_name: stepFormTitles[currentStep],
        });
        registrationCorrelationId.current = createJourneyCorrelationId();
        trackJourneyEvent("registration_started", {
          correlation_id: registrationCorrelationId.current,
          source: "college_finder",
        });
        googleLogin();
      } else {
        setRecommendationError(null);
        updateCollegeFinderMutation.mutate({
          preferences: form.getFieldsValue(true),
        });
      }
    } catch (error) {}
  };

  if (loadingRecommendations) {
    return (
      <Flex className="w-full h-full justify-center items-center">
        <Spin size="large" />
      </Flex>
    );
  }
  return (
    <Flex vertical style={{ width: "100%", marginTop: "50px" }} align="center">
      {contextHolder}
      {recommendationError && (
        <Alert
          className="mb-4 w-full max-w-2xl"
          type="error"
          showIcon
          message={recommendationError.message}
          action={
            <Button
              data-testid="retry-recommendations"
              onClick={() => retryAction.current?.()}
            >
              Retry
            </Button>
          }
        />
      )}
      <Title level={2}>{stepFormTitles[currentStep]}</Title>
      <Form form={form} className="college-finder-form">
        <StepForm
          steps={steps}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          content={stepFormElements[currentStep]}
          onFinish={onFinish}
          form={form}
          onNext={
            (fieldValues) => {
              trackFunnelEvent("college_finder_step_complete", {
                step_index: currentStep,
                step_name: stepFormTitles[currentStep],
                user_state: user?.token ? "authenticated" : "anonymous",
              });

              if (user?.token) {
                ReactGA.send({
                  hitType: "pageview",
                  page: `/college-finder/${currentStep}`,
                });
                updateCollegeFinderPartial.mutate({
                  preferences: fieldValues,
                });
              }
            }
          }
        ></StepForm>
      </Form>
    </Flex>
  );
};
